<template>
    <el-container class="appContainer" :class="{ 'mobile-layout': isMobile }">
        <!-- 移动端：侧边栏改为绝对定位的抽屉 -->
        <el-aside
            width="200px"
            class="app-aside"
            :class="{
				'mobile-aside': isMobile,
				'aside-show': isMobile && isAsideShow,
				'aside-hide': isMobile && !isAsideShow
			}"
        >
            <appSidebar></appSidebar>
        </el-aside>

        <!-- 主内容容器 -->
        <el-container class="main-container">
            <!-- 头部 -->
            <el-header class="app-header" style="border-bottom: 3px solid #e4e7ed">
                <appHeader></appHeader>
            </el-header>

            <!-- 移动端遮罩层 -->
            <div
                class="mobile-mask"
                v-if="isMobile && isAsideShow"
                @click="toggleAside"
            ></div>

            <!-- 主内容区 -->
            <el-main ref="mainRef" class="app-main">
                <appMain></appMain>
            </el-main>
        </el-container>
    </el-container>
</template>

<script>
import { computed, onMounted, provide, onBeforeUnmount, ref, watch } from "vue";
import appHeader from "./appHeader.vue";
import appMain from "./appMain.vue";
import appSidebar from "./appSidebar.vue";

export default {
    name: "container",
    components: {
        appHeader,
        appMain,
        appSidebar,
    },
    setup() {
        // 响应式状态
        const isMobile = ref(false); // 是否为移动端
        const isAsideShow = ref(true); // 移动端侧边栏是否显示
        const mainRef = ref(null);

        // 用户信息
        const userInfo = computed(() => {
            return localStorage.getItem("userInfo")
                ? JSON.parse(localStorage.getItem("userInfo"))
                : {};
        });

        // 提供用户信息给子组件
        provide("userInfo", userInfo);

        // 提供侧边栏切换方法给子组件（供header中的菜单按钮调用）
        const toggleAside = () => {
            isAsideShow.value = !isAsideShow.value;
        };
        provide("toggleAside", toggleAside);

        // 检测屏幕尺寸
        const checkScreenSize = () => {
            const width = window.innerWidth;
            isMobile.value = width < 768; // 768px以下为移动端

            // 大屏默认显示侧边栏，小屏默认隐藏
            if (!isMobile.value) {
                isAsideShow.value = true;
            } else {
                isAsideShow.value = false;
            }
        };

        // 监听路由变化（移动端自动隐藏侧边栏）
        watch(
            () => isAsideShow.value,
            (val) => {
                // 移动端切换侧边栏时，设置body滚动/禁止滚动
                if (isMobile.value) {
                    document.body.style.overflow = val ? "hidden" : "auto";
                }
            }
        );

        // 生命周期
        onMounted(() => {
            checkScreenSize();
            window.addEventListener("resize", checkScreenSize);
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", checkScreenSize);
            document.body.style.overflow = "auto"; // 恢复body滚动
        });

        return {
            mainRef,
            isMobile,
            isAsideShow,
            toggleAside
        };
    },
};
</script>

<style scoped>
/* 基础布局样式 */
.appContainer {
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

/* 侧边栏基础样式 */
.app-aside {
    background-color: #2d3a4b;
    transition: all 0.3s ease;
    z-index: 100;
}

/* 主内容容器 */
.main-container {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* 头部样式 */
.app-header {
    padding: 0;
    height: auto; /* 自适应头部高度 */
}

/* 主内容区 */
.app-main {
    flex: 1;
    overflow-y: auto; /* 主内容区滚动 */
    padding: 16px;
    background-color: #f5f7fa;
    height: calc(100vh - 60px); /* 适配移动端头部高度 */
}

/* 移动端布局适配 */
.mobile-layout {
    /* 移动端使用相对定位，让侧边栏可以绝对定位 */
    position: relative;
}

/* 移动端侧边栏样式 */
.mobile-aside {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 999; /* 高于遮罩层 */
}

/* 移动端侧边栏显示/隐藏 */
.aside-show {
    transform: translateX(0);
}

.aside-hide {
    transform: translateX(-100%);
}

/* 移动端遮罩层 */
.mobile-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
    transition: opacity 0.3s ease;
}

/* 响应式断点 - 移动端 (768px以下) */
@media screen and (max-width: 767px) {
    .app-main {
        padding: 12px;
        height: calc(100vh - 60px); /* 匹配移动端header高度 */
        width: 100vw;
    }

    /* 移动端隐藏默认的aside宽度，使用绝对定位的宽度 */
    ::v-deep .el-aside {
        width: 240px !important; /* 移动端侧边栏宽度调整为240px */
    }

    /* 主容器适配 */
    ::v-deep .el-container {
        display: block; /* 移动端改为块级布局 */
    }
}

/* 平板端适配 (768px - 1024px) */
@media screen and (min-width: 768px) and (max-width: 1024px) {
    .app-aside {
        width: 180px !important; /* 平板端侧边栏宽度 */
    }

    .app-main {
        padding: 14px;
        height: calc(100vh - 70px); /* 匹配平板端header高度 */
    }
}

/* 大屏样式 (1025px以上) */
@media screen and (min-width: 1025px) {
    /* 隐藏移动端遮罩层 */
    .mobile-mask {
        display: none;
    }

    .app-main {
        height: calc(100vh - 80px); /* 匹配桌面端header高度 */
    }

    /* 恢复Element UI默认布局 */
    ::v-deep .el-container {
        display: flex;
    }
}

/* 滚动条优化 */
.app-main::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.app-main::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
}

.app-main::-webkit-scrollbar-track {
    background-color: transparent;
}

/* 适配小屏幕的内容区 */
@media screen and (max-width: 480px) {
    .app-main {
        padding: 8px;
    }

    ::v-deep .el-aside {
        width: 220px !important; /* 小屏手机侧边栏宽度 */
    }
}
</style>
